import React, { useState, useEffect, useCallback } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL } from './config';
import api from './services/api';
import StoricoPrenotazioni from './StoricoPrenotazioni';
import ProfileDetailsForm from './components/profile/ProfileDetailsForm';
import ProfileImageTab from './components/profile/ProfileImageTab';
import GalleriaTab from './components/profile/GalleriaTab';

const INITIAL_AZIENDA = {
  idAzienda: null,
  nomeAzienda: '',
  comune: '',
  indirizzo: '',
  descrizione: '',
  longitudine: '',
  latitudine: '',
  tipologie: '',
  partitaIva: '',
  iban: '',
  immagineProfilo: null,
  galleria: [],
};

const ProfilePage = () => {
  const [azienda, setAzienda] = useState(INITIAL_AZIENDA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [galleria, setGalleria] = useState([]);
  const [loadingGalleria, setLoadingGalleria] = useState(false);
  const [errorGalleria, setErrorGalleria] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const response = await api.getAzienda();
        if (!controller.signal.aborted) setAzienda(response.data);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          setError('Errore nel recupero dei dati');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setAzienda((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await api.updateAzienda(azienda);
      toast.success('Dati aggiornati con successo!');
    } catch {
      toast.error("Errore durante l'invio dei dati");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileImageUpload = async (file) => {
    try {
      const response = await api.uploadProfileImage(file);
      setAzienda((prev) => ({
        ...prev,
        immagineProfilo: response.data.url,
      }));
      toast.success('Immagine profilo caricata');
    } catch {
      toast.error("Errore durante il caricamento dell'immagine");
    }
  };

  const caricaGalleria = useCallback(async () => {
    setLoadingGalleria(true);
    setErrorGalleria(null);
    try {
      const response = await api.getGalleria();
      setGalleria(response.data);
    } catch {
      setErrorGalleria('Errore nel caricamento della galleria');
    } finally {
      setLoadingGalleria(false);
    }
  }, []);

  const handleGalleryUpload = async (files) => {
    try {
      await api.uploadGallery(files);
      toast.success('Immagini caricate');
      caricaGalleria();
    } catch {
      toast.error('Errore durante il caricamento della galleria');
    }
  };

  const handleDeleteImage = async (idImmagine) => {
    try {
      await api.deleteImmagine(idImmagine);
      caricaGalleria();
    } catch {
      toast.error("Errore durante l'eliminazione dell'immagine");
    }
  };

  if (loading) return <div>Caricamento...</div>;
  if (error) return <div>{error}</div>;

  const profileImageSrc =
    azienda.immagineProfilo && azienda.idAzienda
      ? `${BASE_URL}/immagini/azienda/profile/${azienda.idAzienda}`
      : null;

  return (
    <>
      <Tabs
        onSelect={(index) => {
          if (index === 2) caricaGalleria();
        }}
      >
        <TabList>
          <Tab>Dettagli Profilo</Tab>
          <Tab>Immagine Profilo</Tab>
          <Tab>Galleria</Tab>
          <Tab>Prenotazioni</Tab>
        </TabList>

        <TabPanel>
          <ProfileDetailsForm
            azienda={azienda}
            onChange={handleChange}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        </TabPanel>

        <TabPanel>
          <ProfileImageTab
            imageSrc={profileImageSrc}
            onUpload={handleProfileImageUpload}
          />
        </TabPanel>

        <TabPanel>
          <GalleriaTab
            galleria={galleria}
            loading={loadingGalleria}
            error={errorGalleria}
            onUpload={handleGalleryUpload}
            onDelete={handleDeleteImage}
          />
        </TabPanel>

        <TabPanel>
          {/* Stesso endpoint usato dall'admin: il backend limita da solo
              i risultati all'azienda dell'utente collegato. */}
          <StoricoPrenotazioni idAzienda={azienda.idAzienda} />
        </TabPanel>
      </Tabs>
      <ToastContainer />
    </>
  );
};

export default ProfilePage;
